// src/pages/AdminReportSuperBilling.js
import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { NavLink, useLocation } from "react-router-dom";
import { toast } from "react-toastify";

import Breadcrumbs from "@mui/material/Breadcrumbs";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import dayjs from "dayjs";
import { Card, Modal, Table, Spinner } from "react-bootstrap";

import { BASE_URL, security_key as SECURITY_KEY } from "../global";

const GRID_HEIGHT = 640;
const STUDENTS_PER_PAGE = 25;

// Country IDs
const COUNTRY = {
    SINGAPORE: 1,
    UNITED_KINGDOM: 183,
    NEW_ZEALAND: 184,
    AUSTRALIA: 185,
    SCOTLAND: 189,
};

const COUNTRY_CURRENCY_POUNDS = [COUNTRY.UNITED_KINGDOM, COUNTRY.SCOTLAND];

const symbolForCountry = (cent_country) =>
    COUNTRY_CURRENCY_POUNDS.includes(Number(cent_country)) ? "£" : "$";

const formatMoneyRaw = (amount) => {
    const n = Number(amount) || 0;
    return n.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

const formatMoney = (amount, cent_country) => {
    const sym = symbolForCountry(cent_country);
    return `${sym}${formatMoneyRaw(amount)}`;
};

const formatSubscription = (s) => {
    if (!s) return "Nil";
    return String(s)
        .split("_")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(" ");
};

const endOfMonthStr = (ym) =>
    dayjs(`${ym}-01`).endOf("month").format("YYYY-MM-DD");

// which columns should open the student modal on click
const SESSION_CLICK_FIELDS = [
    "total_math_sessions",
    "total_english_sessions",
    "total_other_sessions",
    "total_combined_sessions",
    "total_lessons_nos",
];

// Attendance status mapping
const ATTENDANCE_STATUS_LABELS = {
    0: "Upcoming",
    1: "Ongoing",
    2: "Attended",
    3: "Missed",
    4: "Offline (Not Used)",
    5: "Defer (Not Used)",
    6: "Cancelled",
};

const formatAttendanceStatus = (code) => {
    if (code === null || code === undefined) return "";
    const num = Number(code);
    if (Object.prototype.hasOwnProperty.call(ATTENDANCE_STATUS_LABELS, num)) {
        return ATTENDANCE_STATUS_LABELS[num];
    }
    return String(code);
};

// Paused sessions share attendance_status 6 with cancellations, so the
// is_paused flag is what distinguishes them. Show "Paused" for those; they
// are displayed for transparency but excluded from billable counts/charges.
const isSessionPaused = (row) => Number(row?.is_paused) === 1;
const formatSessionStatus = (row) =>
    isSessionPaused(row) ? "Paused" : formatAttendanceStatus(row?.attendance_status);

// CSV helpers
const sanitizeForFilename = (name) =>
    (name || "").replace(/[\/\\?%*:|"<>]/g, "_");

const exportToCsv = (filename, rows, headers) => {
    if (!rows || rows.length === 0) return;

    const escapeValue = (value) => {
        if (value === null || value === undefined) return "";
        const str = String(value);
        if (/[",\r\n]/.test(str)) {
            return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
    };

    const headerLine = headers.map((h) => escapeValue(h.label)).join(",");
    const lines = rows.map((row) =>
        headers.map((h) => escapeValue(row[h.key])).join(",")
    );
    const csvContent = [headerLine, ...lines].join("\r\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

export default function AdminReportSuperBilling() {
    const location = useLocation();

    const [loading, setLoading] = useState(false);

    const [serviceRowsRaw, setServiceRowsRaw] = useState([]);
    const [licenceRowsRaw, setLicenceRowsRaw] = useState([]);

    const initialMonth = dayjs().format("YYYY-MM");
    const [billMonth, setBillMonth] = useState(initialMonth);
    const [refDate, setRefDate] = useState(endOfMonthStr(initialMonth));

    // tab: filter by centre type, not subscription type
    // values: "all" | "trial" | "license"
    const [tab, setTab] = useState("all");

    // Student drilldown modal state
    const [studentModalOpen, setStudentModalOpen] = useState(false);
    const [studentModalCentre, setStudentModalCentre] = useState(null);
    const [studentList, setStudentList] = useState([]);
    const [studentSessions, setStudentSessions] = useState([]);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [studentLoading, setStudentLoading] = useState(false);
    const [sessionsLoading, setSessionsLoading] = useState(false);
    const [studentPage, setStudentPage] = useState(1);

    // VC session breakdown modal state
    const [vcModalOpen, setVcModalOpen] = useState(false);
    const [vcModalCentre, setVcModalCentre] = useState(null);
    const [vcSessions, setVcSessions] = useState([]);
    const [vcLoading, setVcLoading] = useState(false);

    // Per-session user breakdown state
    const [vcUserBreakdown, setVcUserBreakdown] = useState([]);
    const [vcUserLoading, setVcUserLoading] = useState(false);
    const [selectedVcSession, setSelectedVcSession] = useState(null);

    useEffect(() => {
        sessionStorage.setItem("previousPageAdminPathname", location.pathname);
    }, [location.pathname]);

    useEffect(() => {
        loadAll();
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        const wantRef = endOfMonthStr(billMonth);
        if (refDate !== wantRef) setRefDate(wantRef);
        // eslint-disable-next-line
    }, [billMonth]);

    useEffect(() => {
        const ym = dayjs(refDate).format("YYYY-MM");
        if (billMonth !== ym) setBillMonth(ym);
        // eslint-disable-next-line
    }, [refDate]);

    async function loadAll(date = refDate, month = billMonth) {
        setLoading(true);
        try {
            const res = await axios.post(BASE_URL + "API/api_index.php", {
                security_key: SECURITY_KEY,
                mode: "getSuperAdminBillingReport",
                ref_date: date, // YYYY-MM-DD (end of month)
                month, // YYYY-MM
                fk_cent_key: "", // all centres
            });
            const payload = res?.data?.data || {};
            const svcData = Array.isArray(payload.service_rows)
                ? payload.service_rows
                : [];
            const licData = Array.isArray(payload.licence_rows)
                ? payload.licence_rows
                : [];

            // service rows: one per centre
            const mappedSvc = svcData.map((r, i) => {
                const centIsTrial = Number(r.cent_is_trial_account || 0) === 1;
                const centreType = centIsTrial ? "trial" : "license";

                // Keep raw service prices so we can compute Games+Enrichment pricing rules in mergedRows
                const gamesPrice = Number(r.games || 0);
                const enrichmentPrice = Number(r.enrichment || 0);
                const bundlePrice = Number(r.games_enrichment || 0);

                return {
                    id: `${r.pk_cent_key || i}`,
                    pk_cent_key: r.pk_cent_key,
                    cent_name: r.cent_name,
                    cent_country: Number(r.cent_country ?? 0),

                    owner_name: r.owner_name || "",
                    subscription_type: r.subscription_type || "",

                    // centre type flags
                    cent_is_trial_account: centIsTrial ? 1 : 0,
                    centre_type: centreType,

                    // service prices (as-of ref date)
                    games_price: gamesPrice,
                    enrichment_price: enrichmentPrice,
                    games_enrichment_price: bundlePrice,
                };
            });
            setServiceRowsRaw(mappedSvc);

            // licensing rows: one per centre per month
            // IMPORTANT: backend now returns games_students / enrichment_students / games_enrichment_students
            // These are ONLY used to decide if there was usage (>0). If used, we charge using students_with_sessions.
            const mappedLic = licData.map((r) => ({
                pk_cent_key: r.pk_cent_key || r.fk_cent_key,
                cent_name: r.cent_name,
                cent_payable_currency: r.cent_payable_currency || "",
                cent_fixed_fee_month: Number(r.cent_fixed_fee_month || 0),

                // subject fee per lesson (still exported for reference)
                cent_english_subject_fee: Number(r.cent_english_subject_fee || 0),
                cent_math_subject_fee: Number(r.cent_math_subject_fee || 0),
                cent_other_subject_fee: Number(r.cent_other_subject_fee || 0),

                total_math_sessions: Number(r.total_math_sessions || 0),
                total_english_sessions: Number(r.total_english_sessions || 0),
                total_other_sessions: Number(r.total_other_sessions || 0),
                total_combined_sessions: Number(r.total_combined_sessions || 0),

                // students with sessions this month (per centre)
                students_with_sessions: Number(r.students_with_sessions || 0),

                games_students: Number(r.games_students || 0),
                enrichment_students: Number(r.enrichment_students || 0),
                games_enrichment_students: Number(r.games_enrichment_students || 0),

                // Video conferencing aggregates from backend
                vc_participants: Number(r.vc_participants || 0),
                vc_additional_participants: Number(
                    r.vc_additional_participants || 0
                ),
                vc_60min_price: Number(r.vc_60min_price || 0),
                vc_additional_block_price: Number(
                    r.vc_additional_block_price || 0
                ),
                vc_amount: Number(r.vc_amount || 0),

                total_amount_payable: Number(r.total_amount_payable || 0),
                payment_status: Number(r.payment_status ?? 3),
                month_end_date: r.month_end_date || null,
                pk_cps_key: r.pk_cps_key || null,
                pk_cps_id: r.pk_cps_id || null,
                _raw: r,
            }));
            setLicenceRowsRaw(mappedLic);
        } catch (e) {
            console.error(e);
            toast.error("Failed to load billing report data");
            setServiceRowsRaw([]);
            setLicenceRowsRaw([]);
        } finally {
            setLoading(false);
        }
    }

    // ---- Student drilldown: backend calls ----

    async function loadCentreStudents(fk_cent_key) {
        setStudentLoading(true);
        setStudentList([]);
        setStudentSessions([]);
        setSelectedStudent(null);
        setStudentPage(1);

        try {
            const res = await axios.post(BASE_URL + "API/api_index.php", {
                security_key: SECURITY_KEY,
                mode: "getCentreMonthlyActiveStudents",
                month: billMonth, // YYYY-MM
                fk_cent_key,
            });

            if (res.data && res.data.success) {
                setStudentList(res.data.data || []);
            } else {
                toast.error(
                    res.data.message || "Failed to load students for this centre"
                );
            }
        } catch (e) {
            console.error(e);
            toast.error("Error loading students for this centre");
        } finally {
            setStudentLoading(false);
        }
    }

    async function openStudentModalForCentre(row) {
        setStudentModalCentre({
            pk_cent_key: row.pk_cent_key,
            cent_name: row.centre,
        });
        setStudentModalOpen(true);
        setStudentPage(1);
        await loadCentreStudents(row.pk_cent_key);
    }

    async function loadStudentSessions(student) {
        if (!studentModalCentre) return;

        setSessionsLoading(true);
        setStudentSessions([]);
        setSelectedStudent(student);

        try {
            const res = await axios.post(BASE_URL + "API/api_index.php", {
                security_key: SECURITY_KEY,
                mode: "getCentreMonthlyStudentSessions",
                month: billMonth,
                fk_cent_key: studentModalCentre.pk_cent_key,
                fk_student_key: student.pk_student_key,
            });

            if (res.data && res.data.success) {
                setStudentSessions(res.data.data || []);
            } else {
                toast.error(res.data.message || "Failed to load sessions for student");
            }
        } catch (e) {
            console.error(e);
            toast.error("Error loading sessions for student");
        } finally {
            setSessionsLoading(false);
        }
    }

    // ---- VC session breakdown: backend calls ----

    async function loadVcSessions(fk_cent_key) {
        setVcLoading(true);
        setVcSessions([]);
        setVcUserBreakdown([]);
        setSelectedVcSession(null);

        try {
            const res = await axios.post(BASE_URL + "API/api_index.php", {
                security_key: SECURITY_KEY,
                mode: "getVideoConferencingSessionBreakdown",
                month: billMonth,
                fk_cent_key,
            });

            if (res.data && res.data.success) {
                setVcSessions(res.data.data || []);
            } else {
                toast.error(
                    res.data.message ||
                    "Failed to load video conferencing session breakdown"
                );
            }
        } catch (e) {
            console.error(e);
            toast.error("Error loading video conferencing session breakdown");
        } finally {
            setVcLoading(false);
        }
    }

    async function openVcModalForCentre(row) {
        setVcModalCentre({
            pk_cent_key: row.pk_cent_key,
            cent_name: row.centre,
            cent_country: row.cent_country,
        });
        setVcModalOpen(true);
        await loadVcSessions(row.pk_cent_key);
    }

    // ---- VC user breakdown for a selected session ----

    async function loadVcUserBreakdownForSession(sessionRow) {
        if (!vcModalCentre || !sessionRow || !sessionRow.fk_session_key) return;

        setVcUserLoading(true);
        setVcUserBreakdown([]);
        setSelectedVcSession(sessionRow);

        try {
            const res = await axios.post(BASE_URL + "API/api_index.php", {
                security_key: SECURITY_KEY,
                mode: "getVideoConferencingSessionUserBreakdown",
                month: billMonth,
                fk_cent_key: vcModalCentre.pk_cent_key,
                fk_session_key: sessionRow.fk_session_key,
            });

            if (res.data && res.data.success) {
                setVcUserBreakdown(res.data.data || []);
            } else {
                toast.error(
                    res.data.message || "Failed to load per-user VC breakdown for session"
                );
            }
        } catch (e) {
            console.error(e);
            toast.error("Error loading per-user VC breakdown for session");
        } finally {
            setVcUserLoading(false);
        }
    }

    // Open modals when clicking on certain columns
    const handleCellClick = (params) => {
        if (!params || !params.field) return;
        const row = params.row;
        if (!row || !row.pk_cent_key) return;

        if (SESSION_CLICK_FIELDS.includes(params.field)) {
            openStudentModalForCentre(row);
        } else if (params.field === "vc_amount" && Number(row.vc_amount || 0) > 0) {
            openVcModalForCentre(row);
        }
    };

    // Join service + licensing into a single billing row that matches the Excel layout
    const mergedRows = useMemo(() => {
        const licByCent = new Map(licenceRowsRaw.map((x) => [x.pk_cent_key, x]));

        // New flat fees
        const PER_LESSON_FEE = 3; // Per Lesson (£) for all centres
        const VC_60MIN_FEE = 0.75; // VC 60 mins (£) per user
        const VC_ADDITIONAL_BLOCK_FEE = 0.15; // Additional charges per 15-min block (£)

        return serviceRowsRaw.map((svc) => {
            const lic = licByCent.get(svc.pk_cent_key);

            const total_math_sessions = lic?.total_math_sessions ?? 0;
            const total_english_sessions = lic?.total_english_sessions ?? 0;
            const total_other_sessions = lic?.total_other_sessions ?? 0;
            const total_combined_sessions = lic?.total_combined_sessions ?? 0;

            const total_lessons_nos =
                total_math_sessions +
                total_english_sessions +
                total_other_sessions +
                total_combined_sessions;

            // NEW: flat per-lesson fee applied to all sessions
            const lessons_amount = total_lessons_nos * PER_LESSON_FEE;

            const fixed_fee = lic?.cent_fixed_fee_month ?? 0;

            // students with sessions this month (per centre)
            const students_with_sessions = lic?.students_with_sessions ?? 0;

            const games_students_used = lic?.games_students ?? 0;
            const enrichment_students_used = lic?.enrichment_students ?? 0;
            const games_enrichment_students_used = lic?.games_enrichment_students ?? 0;


            const hasBundle = Number(svc.games_enrichment_price || 0) > 0;
            const hasBoth =
                Number(svc.games_price || 0) > 0 && Number(svc.enrichment_price || 0) > 0;

            const has_ge_subscription = hasBundle || hasBoth;

            // price: bundle if present, else games + enrichment
            const ge_price = hasBundle
                ? Number(svc.games_enrichment_price || 0)
                : Number(svc.games_price || 0) + Number(svc.enrichment_price || 0);

            const has_ge_usage = Number(games_enrichment_students_used || 0) > 0;

            const ge_students =
                has_ge_subscription && ge_price > 0 && has_ge_usage
                    ? students_with_sessions
                    : 0;

            const ge_amount = ge_students * ge_price;

            // Video conferencing – from licence rows
            const vc_participants = lic?.vc_participants ?? 0;
            const vc_additional_participants = lic?.vc_additional_participants ?? 0;
            const vc_amount = lic?.vc_amount ?? 0;

            // show unit prices in the main table
            const vc_60min_price = vc_participants > 0 ? VC_60MIN_FEE : 0;
            const vc_additional_block_price =
                vc_additional_participants > 0 ? VC_ADDITIONAL_BLOCK_FEE : 0;

            // Grand Total = Lessons + Games & Enrichment + VC + Fixed
            const grand_total = lessons_amount + ge_amount + vc_amount + fixed_fee;

            return {
                id: svc.id,
                pk_cent_key: svc.pk_cent_key,
                cent_country: svc.cent_country,

                centre: svc.cent_name,
                owner_name: svc.owner_name || "",

                subscription_type: svc.subscription_type,
                centre_type: svc.centre_type, // "trial" | "license"
                cent_is_trial_account: svc.cent_is_trial_account,

                total_math_sessions,
                total_english_sessions,
                total_other_sessions,
                total_combined_sessions,
                total_lessons_nos,
                per_lesson_fee: PER_LESSON_FEE,
                lessons_amount,

                students_with_sessions,

                // show usage counts (verification / audit) — kept for exports / future UI
                games_students_used,
                enrichment_students_used,
                games_enrichment_students_used,

                ge_students,
                ge_price,
                ge_amount,

                vc_participants,
                vc_60min_price,
                vc_additional_participants,
                vc_additional_block_price,
                vc_amount,

                fixed_fee,
                grand_total,
            };
        });
    }, [serviceRowsRaw, licenceRowsRaw]);

    // Filter by centre type tab
    const filteredRows = useMemo(() => {
        return mergedRows.filter((r) => {
            const t = (r.centre_type || "").toLowerCase();
            if (tab === "all") return true;
            if (tab === "trial") return t === "trial";
            if (tab === "license") return t === "license";
            return true;
        });
    }, [mergedRows, tab]);

    const grandTotals = useMemo(() => {
        let uk = 0;
        let nonUk = 0;
        for (const r of filteredRows) {
            if (COUNTRY_CURRENCY_POUNDS.includes(Number(r.cent_country)))
                uk += Number(r.grand_total) || 0;
            else nonUk += Number(r.grand_total) || 0;
        }
        return { uk, nonUk };
    }, [filteredRows]);

    const csvBase = `SuperAdmin_Billing_${refDate}_${billMonth}_${tab}`;

    const tabLabel = useMemo(() => {
        if (tab === "trial") return "Trial centres";
        if (tab === "license") return "Licensed centres";
        return "All centres";
    }, [tab]);

    // ---- MASS EXPORT: 4 CSVs per centre (Main + Students + VC Sessions + VC Users) ----
    const handleExportCentreAll = useCallback(
        async (row) => {
            if (!row || !row.pk_cent_key) {
                toast.error("No centre key for this row; cannot export.");
                return;
            }

            const fk_cent_key = row.pk_cent_key;
            const centreName = row.centre || "";
            const fileCentreName = sanitizeForFilename(centreName);

            // Match licence row so we can include subject fees / currency in main summary
            const licRow =
                licenceRowsRaw.find((x) => x.pk_cent_key === fk_cent_key) || null;

            try {
                toast.info(`Exporting CSVs for ${centreName} (${billMonth})…`);

                const [studentSessionsRes, vcSessionsRes, vcFullRes] =
                    await Promise.all([
                        axios.post(BASE_URL + "API/api_index.php", {
                            security_key: SECURITY_KEY,
                            mode: "getCentreMonthlyAllStudentSessions",
                            month: billMonth,
                            fk_cent_key,
                        }),
                        axios.post(BASE_URL + "API/api_index.php", {
                            security_key: SECURITY_KEY,
                            mode: "getVideoConferencingSessionBreakdown",
                            month: billMonth,
                            fk_cent_key,
                        }),
                        axios.post(BASE_URL + "API/api_index.php", {
                            security_key: SECURITY_KEY,
                            mode: "getVideoConferencingSessionUserBreakdownAll",
                            month: billMonth,
                            fk_cent_key,
                        }),
                    ]);

                // Basic guards
                if (!studentSessionsRes.data || !studentSessionsRes.data.success) {
                    throw new Error(
                        studentSessionsRes.data?.message ||
                        "Failed to load student sessions for export"
                    );
                }
                if (!vcSessionsRes.data || !vcSessionsRes.data.success) {
                    throw new Error(
                        vcSessionsRes.data?.message ||
                        "Failed to load VC session summary for export"
                    );
                }
                if (!vcFullRes.data || !vcFullRes.data.success) {
                    throw new Error(
                        vcFullRes.data?.message ||
                        "Failed to load VC breakdown for export"
                    );
                }

                const studentSessionRaw = Array.isArray(studentSessionsRes.data.data)
                    ? studentSessionsRes.data.data
                    : [];
                const vcSessionRaw = Array.isArray(vcSessionsRes.data.data)
                    ? vcSessionsRes.data.data
                    : [];
                const vcRaw = Array.isArray(vcFullRes.data.data)
                    ? vcFullRes.data.data
                    : [];

                // -----------------------------
                // FILE 1: MAIN BILLING SUMMARY
                // -----------------------------
                const centreSummaryRows = [
                    {
                        billing_month: billMonth,
                        centre: centreName,
                        pk_cent_key: fk_cent_key,

                        owner_name: row.owner_name || "",
                        centre_type: row.centre_type || "",
                        subscription_type: formatSubscription(row.subscription_type),
                        cent_country: row.cent_country ?? "",
                        cent_payable_currency: licRow?.cent_payable_currency || "",

                        // Subject fees per lesson (still for reference)
                        cent_english_subject_fee: licRow?.cent_english_subject_fee ?? "",
                        cent_math_subject_fee: licRow?.cent_math_subject_fee ?? "",
                        cent_other_subject_fee: licRow?.cent_other_subject_fee ?? "",

                        // Lesson counts / amounts
                        students_with_sessions: row.students_with_sessions ?? "",
                        total_math_sessions: row.total_math_sessions ?? "",
                        total_english_sessions: row.total_english_sessions ?? "",
                        total_other_sessions: row.total_other_sessions ?? "",
                        total_combined_sessions: row.total_combined_sessions ?? "",
                        total_lessons_nos: row.total_lessons_nos ?? "",
                        per_lesson_fee: row.per_lesson_fee ?? "",
                        lessons_amount: row.lessons_amount ?? "",

                        games_students_used: row.games_students_used ?? "",
                        enrichment_students_used: row.enrichment_students_used ?? "",
                        games_enrichment_students_used:
                            row.games_enrichment_students_used ?? "",

                        // Games + Enrichment
                        ge_students: row.ge_students ?? "",
                        ge_price: row.ge_price ?? "",
                        ge_amount: row.ge_amount ?? "",

                        // VC aggregates
                        vc_participants: row.vc_participants ?? "",
                        vc_60min_price: row.vc_60min_price ?? "",
                        vc_additional_participants: row.vc_additional_participants ?? "",
                        vc_additional_block_price: row.vc_additional_block_price ?? "",
                        vc_amount: row.vc_amount ?? "",

                        // Fixed fee + grand total
                        fixed_fee: row.fixed_fee ?? "",
                        grand_total: row.grand_total ?? "",

                        month_end_date: licRow?.month_end_date || "",
                    },
                ];

                const centreSummaryHeaders = [
                    { key: "billing_month", label: "Billing Month" },
                    { key: "centre", label: "Centre" },
                    { key: "pk_cent_key", label: "Centre Key" },

                    { key: "owner_name", label: "Owner / Director" },
                    { key: "centre_type", label: "Centre Type" },
                    { key: "subscription_type", label: "Addon Subscription" },
                    { key: "cent_country", label: "Country ID" },
                    { key: "cent_payable_currency", label: "Billing Currency" },

                    {
                        key: "cent_english_subject_fee",
                        label: "English Subject Fee (per lesson)",
                    },
                    {
                        key: "cent_math_subject_fee",
                        label: "Math Subject Fee (per lesson)",
                    },
                    {
                        key: "cent_other_subject_fee",
                        label: "Other Subject Fee (per lesson)",
                    },

                    {
                        key: "students_with_sessions",
                        label: "Students with Sessions (Nos)",
                    },
                    {
                        key: "total_math_sessions",
                        label: "No. of Maths Scheduled Sessions",
                    },
                    {
                        key: "total_english_sessions",
                        label: "No. of Eng. Scheduled Sessions",
                    },
                    {
                        key: "total_other_sessions",
                        label: "No. of Other Subject Scheduled Sessions",
                    },
                    {
                        key: "total_combined_sessions",
                        label: "No. of Combined Subject Scheduled Sessions",
                    },
                    { key: "total_lessons_nos", label: "Total Lessons (Nos)" },
                    { key: "per_lesson_fee", label: "Per Lesson Fee" },
                    { key: "lessons_amount", label: "Total Lessons Amount" },

                    { key: "games_students_used", label: "Games Usage (distinct students) - check only" },
                    { key: "enrichment_students_used", label: "Enrichment Usage (distinct students) - check only" },
                    { key: "games_enrichment_students_used",  label: "Games+Enrichment Usage (distinct students) - check only"},

                    {
                        key: "ge_students",
                        label: "Games + Enrichment (Students charged) [= students_with_sessions if used]"
                    },
                    {
                        key: "ge_price",
                        label: "Games + Enrichment Per-Student Price"
                    },
                    { key: "ge_amount", label: "Games + Enrichment Amount" },

                    { key: "vc_participants", label: "VC Participants (Total count)" },
                    { key: "vc_60min_price", label: "VC 60 min Price" },
                    {
                        key: "vc_additional_participants",
                        label: "VC Additional Participants per 15 min",
                    },
                    {
                        key: "vc_additional_block_price",
                        label: "VC Additional 15-min Block Price",
                    },
                    { key: "vc_amount", label: "Total VC Amount" },

                    { key: "fixed_fee", label: "Fixed Fee" },
                    { key: "grand_total", label: "Grand Total" },

                    { key: "month_end_date", label: "Month End Date" },
                ];

                // ------------------------------------
                // FILE 2: STUDENT + SESSION BREAKDOWN
                // ------------------------------------
                const studentSessionRows = studentSessionRaw.map((r) => {
                    const studentName = `${r.st_first_name || ""} ${
                        r.st_surname || ""
                    }`.trim();
                    const tutorName = `${r.tut_fname || ""} ${
                        r.tut_surname || ""
                    }`.trim();

                    return {
                        billing_month: billMonth,
                        centre: centreName,

                        student_name: studentName,
                        student_year_level: r.year_level || "",
                        student_session_count:
                            r.session_count != null ? r.session_count : "",

                        tutor_name: tutorName,
                        session_date: r.ses_date,
                        session_start_time: r.ses_start_time,
                        session_end_time: r.ses_end_time,
                        session_attendance_status: formatSessionStatus(r),
                        session_is_paused: isSessionPaused(r) ? "Yes" : "No",
                    };
                });

                const studentSessionHeaders = [
                    { key: "billing_month", label: "Billing Month" },
                    { key: "centre", label: "Centre" },
                    { key: "student_name", label: "Student Name" },
                    { key: "student_year_level", label: "Student Year" },
                    { key: "student_session_count", label: "Billable Sessions in Month" },
                    { key: "tutor_name", label: "Tutor Name" },
                    { key: "session_date", label: "Session Date" },
                    { key: "session_start_time", label: "Session Start Time" },
                    { key: "session_end_time", label: "Session End Time" },
                    { key: "session_attendance_status", label: "Session Attendance Status" },
                    { key: "session_is_paused", label: "Paused (not billed)" },
                ];

                // ------------------------------------
                // FILE 3: VC SESSION SUMMARY (per-session)
                // ------------------------------------
                const vcSessionRows = vcSessionRaw.map((s) => ({
                    billing_month: billMonth,
                    centre: centreName,

                    vc_session_date: s.session_date,
                    vc_session_start_time: s.session_start_time,
                    vc_session_end_time: s.session_end_time,
                    vc_session_participants: s.participant_count,
                    vc_session_standard_block_amount: s.standard_block_amount,
                    vc_session_additional_block_amount: s.additional_block_amount,
                    vc_session_amount: s.session_amount,
                    vc_session_breakdown_total: s.breakdown_total,
                }));

                const vcSessionHeaders = [
                    { key: "billing_month", label: "Billing Month" },
                    { key: "centre", label: "Centre" },
                    { key: "vc_session_date", label: "VC Session Date" },
                    { key: "vc_session_start_time", label: "VC Session Start Time" },
                    { key: "vc_session_end_time", label: "VC Session End Time" },
                    { key: "vc_session_participants", label: "VC Session Participants" },
                    {
                        key: "vc_session_standard_block_amount",
                        label: "VC Session Standard Blocks Amount",
                    },
                    {
                        key: "vc_session_additional_block_amount",
                        label: "VC Session Additional Blocks Amount",
                    },
                    { key: "vc_session_amount", label: "VC Session Total Amount" },
                    { key: "vc_session_breakdown_total", label: "VC Session Breakdown Total" },
                ];

                // ------------------------------------
                // FILE 4: VC PER-USER BREAKDOWN (all sessions)
                // ------------------------------------
                const vcUserRows = vcRaw.map((r) => ({
                    billing_month: billMonth,
                    centre: centreName,

                    vc_session_date: r.session_date,
                    vc_session_start_time: r.session_start_time,
                    vc_session_end_time: r.session_end_time,

                    vc_user_name: r.user_name || "",
                    vc_user_duration_minutes:
                        r.duration_minutes != null ? r.duration_minutes : "",
                    vc_user_standard_amount: r.standard_amount,
                    vc_user_additional_amount: r.additional_amount,
                    vc_user_total_amount: r.total_amount,
                }));

                const vcUserHeaders = [
                    { key: "billing_month", label: "Billing Month" },
                    { key: "centre", label: "Centre" },
                    { key: "vc_session_date", label: "VC Session Date" },
                    { key: "vc_session_start_time", label: "VC Session Start Time" },
                    { key: "vc_session_end_time", label: "VC Session End Time" },
                    { key: "vc_user_name", label: "VC User Name" },
                    { key: "vc_user_duration_minutes", label: "VC User Duration (minutes)" },
                    { key: "vc_user_standard_amount", label: "VC User Standard Block Amount" },
                    { key: "vc_user_additional_amount", label: "VC User Additional Block Amount" },
                    { key: "vc_user_total_amount", label: "VC User Total Amount" },
                ];

                // Trigger downloads
                const base = `Centre_${fileCentreName}_${billMonth}`;

                if (centreSummaryRows.length > 0) {
                    exportToCsv(
                        `${base}_1_MainBillingSummary.csv`,
                        centreSummaryRows,
                        centreSummaryHeaders
                    );
                }
                if (studentSessionRows.length > 0) {
                    exportToCsv(
                        `${base}_2_StudentSessions.csv`,
                        studentSessionRows,
                        studentSessionHeaders
                    );
                }
                if (vcSessionRows.length > 0) {
                    exportToCsv(
                        `${base}_3_VideoConferencingSessions.csv`,
                        vcSessionRows,
                        vcSessionHeaders
                    );
                }
                if (vcUserRows.length > 0) {
                    exportToCsv(
                        `${base}_4_VideoConferencingUsers.csv`,
                        vcUserRows,
                        vcUserHeaders
                    );
                }
            } catch (e) {
                console.error(e);
                toast.error(`Failed to export CSVs for ${centreName}`);
            }
        },
        [billMonth, licenceRowsRaw]
    );

    const columns = useMemo(
        () => [
            {
                field: "centre",
                headerName: "Centre",
                flex: 1.4,
                minWidth: 220,
                headerClassName: "super-app-theme--header",
            },
            {
                field: "owner_name",
                headerName: "Owner / Director",
                flex: 1.2,
                minWidth: 200,
                headerClassName: "super-app-theme--header",
            },
            {
                field: "centre_type",
                headerName: "Centre Type",
                flex: 0.9,
                minWidth: 140,
                headerClassName: "super-app-theme--header",
                valueFormatter: (p) => {
                    const v = (p.value || "").toLowerCase();
                    if (v === "trial") return "Trial Centre";
                    if (v === "license") return "License Centre";
                    return "";
                },
            },
            {
                field: "subscription_type",
                headerName: "Addon Subscription",
                flex: 1.0,
                minWidth: 150,
                headerClassName: "super-app-theme--header",
                valueFormatter: (p) => formatSubscription(p.value),
            },
            {
                field: "students_with_sessions",
                headerName: "Students with Sessions (Nos)",
                type: "number",
                flex: 1.0,
                minWidth: 180,
                headerClassName: "super-app-theme--header",
                valueFormatter: (p) => Number(p.value || 0).toString(),
            },

            //  (Hidden for now) usage counts (distinct students) — used only as a >0 check
            /*
            {
                field: "games_students_used",
                headerName: "Games Usage (distinct students)",
                type: "number",
                flex: 1.1,
                minWidth: 220,
                headerClassName: "super-app-theme--header",
                valueFormatter: (p) => Number(p.value || 0).toString(),
            },
            {
                field: "enrichment_students_used",
                headerName: "Enrichment Usage (distinct students)",
                type: "number",
                flex: 1.2,
                minWidth: 240,
                headerClassName: "super-app-theme--header",
                valueFormatter: (p) => Number(p.value || 0).toString(),
            },
            {
                field: "games_enrichment_students_used",
                headerName: "Games+Enrichment Usage (distinct students)",
                type: "number",
                flex: 1.4,
                minWidth: 280,
                headerClassName: "super-app-theme--header",
                valueFormatter: (p) => Number(p.value || 0).toString(),
            },
            */

            {
                field: "total_math_sessions",
                headerName: "No. of Maths Scheduled Sessions",
                type: "number",
                flex: 1.1,
                minWidth: 200,
                headerClassName: "super-app-theme--header",
                valueFormatter: (p) => Number(p.value || 0).toString(),
            },
            {
                field: "total_english_sessions",
                headerName: "No. of Eng. Scheduled Sessions",
                type: "number",
                flex: 1.1,
                minWidth: 200,
                headerClassName: "super-app-theme--header",
                valueFormatter: (p) => Number(p.value || 0).toString(),
            },
            {
                field: "total_other_sessions",
                headerName: "No. of Other Subject Scheduled Sessions",
                type: "number",
                flex: 1.2,
                minWidth: 210,
                headerClassName: "super-app-theme--header",
                valueFormatter: (p) => Number(p.value || 0).toString(),
            },
            {
                field: "total_combined_sessions",
                headerName: "No. of Combined Subject Scheduled Sessions",
                type: "number",
                flex: 1.2,
                minWidth: 220,
                headerClassName: "super-app-theme--header",
                valueFormatter: (p) => Number(p.value || 0).toString(),
            },
            {
                field: "total_lessons_nos",
                headerName: "Total Lessons (Nos)",
                type: "number",
                flex: 0.9,
                minWidth: 140,
                headerClassName: "super-app-theme--header",
                valueFormatter: (p) => Number(p.value || 0).toString(),
            },
            {
                field: "per_lesson_fee",
                headerName: "Per Lesson (£)",
                flex: 0.8,
                minWidth: 120,
                headerClassName: "super-app-theme--header",
                renderCell: (p) => formatMoney(p.row.per_lesson_fee, p.row.cent_country),
            },
            {
                field: "lessons_amount",
                headerName: "Total Lessons (£)",
                flex: 1.0,
                minWidth: 150,
                headerClassName: "super-app-theme--header",
                renderCell: (p) => formatMoney(p.row.lessons_amount, p.row.cent_country),
            },
            {
                field: "ge_students",
                headerName: "Games + Enrichment (No. of students)",
                type: "number",
                flex: 1.3,
                minWidth: 230,
                headerClassName: "super-app-theme--header",
                valueFormatter: (p) => Number(p.value || 0).toString(),
            },
            {
                field: "ge_price",
                headerName: "Per Games + Enrichment (£)",
                flex: 1.0,
                minWidth: 190,
                headerClassName: "super-app-theme--header",
                renderCell: (p) => formatMoney(p.row.ge_price, p.row.cent_country),
            },
            {
                field: "ge_amount",
                headerName: "Total Games + Enrichment (£)",
                flex: 1.2,
                minWidth: 220,
                headerClassName: "super-app-theme--header",
                renderCell: (p) => formatMoney(p.row.ge_amount, p.row.cent_country),
            },
            {
                field: "vc_participants",
                headerName: "Video Conferencing (No. of Participants)",
                type: "number",
                flex: 1.4,
                minWidth: 260,
                headerClassName: "super-app-theme--header",
                valueFormatter: (p) => Number(p.value || 0).toString(),
            },
            {
                field: "vc_60min_price",
                headerName: "Video Conferencing 60 mins (£)",
                flex: 1.1,
                minWidth: 200,
                headerClassName: "super-app-theme--header",
                renderCell: (p) => formatMoney(p.row.vc_60min_price, p.row.cent_country),
            },
            {
                field: "vc_additional_participants",
                headerName:
                    "Video Conferencing (No. Participants per additional 15 min)",
                type: "number",
                flex: 1.6,
                minWidth: 320,
                headerClassName: "super-app-theme--header",
                valueFormatter: (p) => Number(p.value || 0).toString(),
            },
            {
                field: "vc_additional_block_price",
                headerName: "Additional charges (15 min per block)",
                flex: 1.3,
                minWidth: 240,
                headerClassName: "super-app-theme--header",
                renderCell: (p) =>
                    formatMoney(p.row.vc_additional_block_price, p.row.cent_country),
            },
            {
                field: "vc_amount",
                headerName: "Total Video Conferencing (£)",
                flex: 1.2,
                minWidth: 220,
                headerClassName: "super-app-theme--header",
                renderCell: (p) => formatMoney(p.row.vc_amount, p.row.cent_country),
            },
            {
                field: "fixed_fee",
                headerName: "Fixed Fees (£)",
                flex: 0.9,
                minWidth: 140,
                headerClassName: "super-app-theme--header",
                renderCell: (p) => formatMoney(p.row.fixed_fee, p.row.cent_country),
            },
            {
                field: "grand_total",
                headerName: "Grand Total",
                flex: 1.0,
                minWidth: 150,
                headerClassName: "super-app-theme--header",
                renderCell: (p) => formatMoney(p.row.grand_total, p.row.cent_country),
            },
            {
                field: "export_all",
                headerName: "Export All",
                sortable: false,
                filterable: false,
                flex: 0.8,
                minWidth: 140,
                headerClassName: "super-app-theme--header",
                renderCell: (params) => (
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleExportCentreAll(params.row);
                        }}
                    >
                        Export All
                    </button>
                ),
            },
        ],
        [handleExportCentreAll]
    );

    // ---- CSV export handlers for modals ----

    const handleExportStudentsCsv = () => {
        if (!studentModalCentre || studentList.length === 0) return;
        const rows = studentList.map((stu, idx) => ({
            index: idx + 1,
            student_name: `${stu.st_first_name || ""} ${stu.st_surname || ""}`.trim(),
            year_level: stu.year_level || "",
            session_count: stu.session_count || 0,
            paused_count: stu.paused_count || 0,
        }));
        const headers = [
            { key: "index", label: "#" },
            { key: "student_name", label: "Student" },
            { key: "year_level", label: "Year Level" },
            { key: "session_count", label: "Sessions this month" },
            { key: "paused_count", label: "Paused (not billed)" },
        ];
        const centre = sanitizeForFilename(studentModalCentre.cent_name);
        exportToCsv(`Students_${centre}_${billMonth}.csv`, rows, headers);
    };

    const handleExportStudentSessionsCsv = () => {
        if (!studentModalCentre || !selectedStudent || studentSessions.length === 0)
            return;

        const rows = studentSessions.map((s) => ({
            tutor: `${s.tut_fname || ""} ${s.tut_surname || ""}`.trim(),
            date: s.ses_date,
            start_time: s.ses_start_time,
            end_time: s.ses_end_time,
            status: formatSessionStatus(s),
        }));
        const headers = [
            { key: "tutor", label: "Tutor" },
            { key: "date", label: "Date" },
            { key: "start_time", label: "Start" },
            { key: "end_time", label: "End" },
            { key: "status", label: "Status" },
        ];
        const centre = sanitizeForFilename(studentModalCentre.cent_name);
        const studentName = sanitizeForFilename(
            `${selectedStudent.st_first_name || ""} ${selectedStudent.st_surname || ""}`.trim()
        );
        exportToCsv(`Sessions_${centre}_${studentName}_${billMonth}.csv`, rows, headers);
    };

    const handleExportVcSessionsCsv = () => {
        if (!vcModalCentre || vcSessions.length === 0) return;
        const rows = vcSessions.map((s, idx) => ({
            index: idx + 1,
            date: s.session_date,
            start_time: s.session_start_time,
            end_time: s.session_end_time,
            participants: s.participant_count,
            standard_blocks: s.standard_block_amount,
            additional_blocks: s.additional_block_amount,
            session_amount: s.session_amount,
        }));
        const headers = [
            { key: "index", label: "#" },
            { key: "date", label: "Date" },
            { key: "start_time", label: "Start" },
            { key: "end_time", label: "End" },
            { key: "participants", label: "Participants" },
            { key: "standard_blocks", label: "Standard Blocks (£)" },
            { key: "additional_blocks", label: "Additional Blocks (£)" },
            { key: "session_amount", label: "Session Amount (£)" },
        ];
        const centre = sanitizeForFilename(vcModalCentre.cent_name);
        exportToCsv(`VC_Sessions_${centre}_${billMonth}.csv`, rows, headers);
    };

    const handleExportVcUsersCsv = () => {
        if (!vcModalCentre || !selectedVcSession || vcUserBreakdown.length === 0)
            return;

        const rows = vcUserBreakdown.map((u, idx) => ({
            index: idx + 1,
            user: u.user_name || "",
            duration_minutes: u.duration_minutes != null ? u.duration_minutes : "",
            standard_amount: u.standard_amount,
            additional_amount: u.additional_amount,
            total_amount: u.total_amount,
        }));
        const headers = [
            { key: "index", label: "#" },
            { key: "user", label: "User" },
            { key: "duration_minutes", label: "Duration (minutes)" },
            { key: "standard_amount", label: "Standard Block Amount (£)" },
            { key: "additional_amount", label: "Additional Block Amount (£)" },
            { key: "total_amount", label: "Total Amount (£)" },
        ];
        const centre = sanitizeForFilename(vcModalCentre.cent_name);
        const date = sanitizeForFilename(selectedVcSession.session_date);
        const start = sanitizeForFilename(selectedVcSession.session_start_time);
        exportToCsv(`VC_Users_${centre}_${date}_${start}.csv`, rows, headers);
    };

    return (
        <div className="container">
            {/* Header */}
            <div className="mb-3 d-flex justify-content-between align-items-center">
                <div>
                    <h5 className="page-title">SuperAdmin Billing Report</h5>
                    <Breadcrumbs aria-label="breadcrumb" className="my-breadcrumbs">
                        <NavLink to="/calendar">Home</NavLink>
                        <NavLink to="/admin/AdminReport">Reports</NavLink>
                        <Typography color="text.primary">SuperAdmin Billing</Typography>
                    </Breadcrumbs>
                </div>

                {/* Filters */}
                <div className="d-flex gap-2 align-items-center">
                    <div className="d-flex align-items-center gap-2">
                        <label className="mb-0">As of (pricing):</label>
                        <input
                            type="date"
                            className="form-control"
                            style={{ width: 160 }}
                            value={refDate}
                            onChange={(e) => setRefDate(e.target.value)}
                            title="As of date"
                        />
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        <label className="mb-0">Billing Month:</label>
                        <input
                            type="month"
                            className="form-control"
                            style={{ width: 140 }}
                            value={billMonth}
                            onChange={(e) => setBillMonth(e.target.value)}
                            title="Billing Month"
                        />
                    </div>
                    <button
                        className="btn btn-outline-secondary"
                        onClick={() => loadAll(refDate, billMonth)}
                        disabled={loading}
                    >
                        {loading ? "Refreshing…" : "Apply"}
                    </button>
                </div>
            </div>

            {/* Tabs / filters by centre type */}
            <div className="mb-3">
                <div className="btn-group" role="group" aria-label="Centre type tabs">
                    <button
                        type="button"
                        className={`btn ${
                            tab === "all" ? "btn-primary" : "btn-outline-primary"
                        }`}
                        onClick={() => setTab("all")}
                    >
                        All Centres
                    </button>
                    <button
                        type="button"
                        className={`btn ${
                            tab === "trial" ? "btn-primary" : "btn-outline-primary"
                        }`}
                        onClick={() => setTab("trial")}
                    >
                        Trial Centres
                    </button>
                    <button
                        type="button"
                        className={`btn ${
                            tab === "license" ? "btn-primary" : "btn-outline-primary"
                        }`}
                        onClick={() => setTab("license")}
                    >
                        Licensed Centres
                    </button>
                </div>
            </div>

            <Card className="card my-card mb-3">
                <div className="card-header d-flex justify-content-between align-items-center">
                    <strong>
                        As of {dayjs(refDate).format("YYYY-MM-DD")} — {tabLabel} — Billing{" "}
                        {billMonth}
                    </strong>
                    <span className="text-muted">
                        {grandTotals.uk > 0 && grandTotals.nonUk > 0 ? (
                            <>
                                Grand Totals:&nbsp;
                                <b>£{grandTotals.uk.toLocaleString()}</b> (UK)
                                &nbsp;•&nbsp;
                                <b>${grandTotals.nonUk.toLocaleString()}</b> (Non-UK)
                            </>
                        ) : Number(grandTotals.uk) > 0 ? (
                            <>
                                Grand Total: <b>£{grandTotals.uk.toLocaleString()}</b>
                            </>
                        ) : (
                            <>
                                Grand Total: <b>${grandTotals.nonUk.toLocaleString()}</b>
                            </>
                        )}
                    </span>
                </div>
                <Card.Body>
                    <Box
                        sx={{
                            width: "100%",
                            overflowX: "auto",
                            "& .super-app-theme--header": {
                                backgroundColor: "#f4f4f4",
                                whiteSpace: "normal",
                            },
                            "& .MuiDataGrid-cell": {
                                whiteSpace: "normal",
                                wordWrap: "break-word",
                            },
                            "& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus":
                                {
                                    outline: "none",
                                },
                        }}
                    >
                        <DataGrid
                            sx={{ height: GRID_HEIGHT }}
                            loading={loading}
                            rows={filteredRows}
                            columns={columns}
                            pageSizeOptions={[10, 25, 50, 100]}
                            rowHeight={56}
                            onCellClick={handleCellClick}
                            slots={{
                                loadingOverlay: LinearProgress,
                                toolbar: GridToolbar,
                            }}
                            slotProps={{
                                toolbar: {
                                    csvOptions: { fileName: csvBase },
                                    printOptions: {
                                        fileName: csvBase,
                                        hideFooter: true,
                                        hideToolbar: true,
                                    },
                                    showQuickFilter: true,
                                },
                            }}
                            disableRowSelectionOnClick
                            showColumnVerticalBorder
                            showCellVerticalBorder
                        />
                    </Box>
                </Card.Body>
            </Card>

            {/* Student drilldown modal */}
            <Modal
                show={studentModalOpen}
                onHide={() => {
                    setStudentModalOpen(false);
                    setStudentList([]);
                    setStudentSessions([]);
                    setSelectedStudent(null);
                    setStudentModalCentre(null);
                    setStudentPage(1);
                }}
                size="lg"
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        {studentModalCentre
                            ? `Students with sessions in ${billMonth} — ${studentModalCentre.cent_name}`
                            : "Students with sessions"}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="d-flex justify-content-between align-items-center">
                        <h6>Students</h6>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={handleExportStudentsCsv}
                            disabled={studentLoading || studentList.length === 0}
                        >
                            Export CSV
                        </button>
                    </div>
                    {studentLoading ? (
                        <div className="d-flex justify-content-center my-3">
                            <Spinner animation="border" size="sm" className="me-2" />
                            Loading students…
                        </div>
                    ) : studentList.length === 0 ? (
                        <p className="text-muted">
                            No students found for this centre in this month (non-cancelled
                            sessions only).
                        </p>
                    ) : (
                        (() => {
                            const total = studentList.length;
                            const totalPages = Math.max(
                                1,
                                Math.ceil(total / STUDENTS_PER_PAGE)
                            );
                            const page = Math.min(studentPage, totalPages);
                            const startIndex = (page - 1) * STUDENTS_PER_PAGE;
                            const currentStudents = studentList.slice(
                                startIndex,
                                startIndex + STUDENTS_PER_PAGE
                            );
                            const endIndex = startIndex + currentStudents.length;

                            return (
                                <>
                                    <Table striped bordered hover size="sm">
                                        <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Student</th>
                                            <th>Year Level</th>
                                            <th>Sessions this month</th>
                                            <th>Paused</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {currentStudents.map((stu, idx) => {
                                            const globalIndex = startIndex + idx + 1;
                                            const pausedCount = Number(stu.paused_count) || 0;
                                            const billableCount = Number(stu.session_count) || 0;
                                            const isFullyPaused =
                                                billableCount === 0 && pausedCount > 0;
                                            const isSelected =
                                                selectedStudent &&
                                                selectedStudent.pk_student_key ===
                                                    stu.pk_student_key;
                                            return (
                                                <tr
                                                    key={stu.pk_student_key || globalIndex}
                                                    style={{ cursor: "pointer" }}
                                                    onClick={() => loadStudentSessions(stu)}
                                                    className={
                                                        isSelected
                                                            ? "table-primary"
                                                            : isFullyPaused
                                                            ? "text-muted"
                                                            : ""
                                                    }
                                                >
                                                    <td>{globalIndex}</td>
                                                    <td>{`${stu.st_first_name || ""} ${
                                                        stu.st_surname || ""
                                                    }`}</td>
                                                    <td>{stu.year_level || "-"}</td>
                                                    <td>{billableCount}</td>
                                                    <td>
                                                        {pausedCount > 0 ? (
                                                            <span className="badge bg-secondary">
                                                                {pausedCount}
                                                            </span>
                                                        ) : (
                                                            "-"
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        </tbody>
                                    </Table>
                                    <div className="d-flex justify-content-between align-items-center mt-2">
                                        <small className="text-muted">
                                            Showing {startIndex + 1}–{endIndex} of {total}{" "}
                                            students (Page {page} of {totalPages})
                                        </small>
                                        <div>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-secondary me-2"
                                                disabled={page <= 1}
                                                onClick={() =>
                                                    setStudentPage((prev) =>
                                                        Math.max(1, prev - 1)
                                                    )
                                                }
                                            >
                                                Previous
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-secondary"
                                                disabled={page >= totalPages}
                                                onClick={() =>
                                                    setStudentPage((prev) =>
                                                        Math.min(totalPages, prev + 1)
                                                    )
                                                }
                                            >
                                                Next
                                            </button>
                                        </div>
                                    </div>
                                </>
                            );
                        })()
                    )}

                    <hr />

                    <div className="d-flex justify-content-between align-items-center">
                        <h6>
                            Sessions{" "}
                            {selectedStudent
                                ? `for ${selectedStudent.st_first_name} ${
                                    selectedStudent.st_surname
                                }${
                                    selectedStudent.year_level
                                        ? " (" + selectedStudent.year_level + ")"
                                        : ""
                                }`
                                : ""}
                        </h6>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={handleExportStudentSessionsCsv}
                            disabled={
                                sessionsLoading ||
                                !selectedStudent ||
                                studentSessions.length === 0
                            }
                        >
                            Export CSV
                        </button>
                    </div>
                    {sessionsLoading ? (
                        <div className="d-flex justify-content-center my-3">
                            <Spinner animation="border" size="sm" className="me-2" />
                            Loading sessions…
                        </div>
                    ) : !selectedStudent ? (
                        <p className="text-muted">
                            Click a student above to see their sessions for that month.
                            Paused sessions are shown greyed out and are not billed.
                        </p>
                    ) : studentSessions.length === 0 ? (
                        <p className="text-muted">
                            No sessions for this student in {billMonth}.
                        </p>
                    ) : (
                        <Table striped bordered hover size="sm">
                            <thead>
                            <tr>
                                <th>Tutor</th>
                                <th>Date</th>
                                <th>Start</th>
                                <th>End</th>
                                <th>Status</th>
                            </tr>
                            </thead>
                            <tbody>
                            {studentSessions.map((s, idx) => {
                                const tutorName = `${s.tut_fname || ""} ${
                                    s.tut_surname || ""
                                }`.trim();
                                const paused = isSessionPaused(s);
                                return (
                                    <tr
                                        key={idx}
                                        className={paused ? "text-muted" : ""}
                                    >
                                        <td>{tutorName || "-"}</td>
                                        <td>{s.ses_date}</td>
                                        <td>{s.ses_start_time}</td>
                                        <td>{s.ses_end_time}</td>
                                        <td>
                                            {paused ? (
                                                <span className="badge bg-secondary">
                                                    Paused
                                                </span>
                                            ) : (
                                                formatAttendanceStatus(s.attendance_status)
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                            </tbody>
                        </Table>
                    )}
                </Modal.Body>
            </Modal>

            {/* VC session breakdown modal */}
            <Modal
                show={vcModalOpen}
                onHide={() => {
                    setVcModalOpen(false);
                    setVcSessions([]);
                    setVcModalCentre(null);
                    setVcUserBreakdown([]);
                    setSelectedVcSession(null);
                }}
                size="lg"
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        {vcModalCentre
                            ? `Video Conferencing sessions in ${billMonth} — ${vcModalCentre.cent_name}`
                            : "Video Conferencing sessions"}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="d-flex justify-content-between align-items-center">
                        <h6>Session breakdown</h6>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={handleExportVcSessionsCsv}
                            disabled={vcLoading || vcSessions.length === 0}
                        >
                            Export CSV
                        </button>
                    </div>
                    {vcLoading ? (
                        <div className="d-flex justify-content-center my-3">
                            <Spinner animation="border" size="sm" className="me-2" />
                            Loading video conferencing sessions…
                        </div>
                    ) : vcSessions.length === 0 ? (
                        <p className="text-muted">
                            No video conferencing sessions found for this centre in this
                            month.
                        </p>
                    ) : (
                        <Table striped bordered hover size="sm">
                            <thead>
                            <tr>
                                <th>Date</th>
                                <th>Start</th>
                                <th>End</th>
                                <th>Participants</th>
                                <th>Standard Blocks (£)</th>
                                <th>Additional Blocks (£)</th>
                                <th>Session Amount (£)</th>
                            </tr>
                            </thead>
                            <tbody>
                            {vcSessions.map((s, idx) => (
                                <tr
                                    key={idx}
                                    style={{ cursor: "pointer" }}
                                    onClick={() => loadVcUserBreakdownForSession(s)}
                                    className={
                                        selectedVcSession &&
                                        selectedVcSession.fk_session_key ===
                                        s.fk_session_key
                                            ? "table-primary"
                                            : ""
                                    }
                                >
                                    <td>{s.session_date}</td>
                                    <td>{s.session_start_time}</td>
                                    <td>{s.session_end_time}</td>
                                    <td>{s.participant_count}</td>
                                    <td>
                                        {vcModalCentre
                                            ? formatMoney(
                                                s.standard_block_amount,
                                                vcModalCentre.cent_country
                                            )
                                            : s.standard_block_amount}
                                    </td>
                                    <td>
                                        {vcModalCentre
                                            ? formatMoney(
                                                s.additional_block_amount,
                                                vcModalCentre.cent_country
                                            )
                                            : s.additional_block_amount}
                                    </td>
                                    <td>
                                        {vcModalCentre
                                            ? formatMoney(
                                                s.session_amount,
                                                vcModalCentre.cent_country
                                            )
                                            : s.session_amount}
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </Table>
                    )}

                    <hr />

                    <div className="d-flex justify-content-between align-items-center">
                        <h6>
                            Per-user breakdown{" "}
                            {selectedVcSession
                                ? `for session on ${selectedVcSession.session_date} (${selectedVcSession.session_start_time}–${selectedVcSession.session_end_time})`
                                : ""}
                        </h6>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={handleExportVcUsersCsv}
                            disabled={
                                vcUserLoading ||
                                !selectedVcSession ||
                                vcUserBreakdown.length === 0
                            }
                        >
                            Export CSV
                        </button>
                    </div>
                    {selectedVcSession == null ? (
                        <p className="text-muted">
                            Click a session above to see the user-level breakdown (user name,
                            duration in minutes, and 3 amounts) for that video conference.
                        </p>
                    ) : vcUserLoading ? (
                        <div className="d-flex justify-content-center my-3">
                            <Spinner animation="border" size="sm" className="me-2" />
                            Loading user breakdown…
                        </div>
                    ) : vcUserBreakdown.length === 0 ? (
                        <p className="text-muted">
                            No per-user breakdown entries found for this session.
                        </p>
                    ) : (
                        <Table striped bordered hover size="sm">
                            <thead>
                            <tr>
                                <th>User</th>
                                <th>Duration (minutes)</th>
                                <th>Standard Block Amount (£)</th>
                                <th>Additional Block Amount (£)</th>
                                <th>Total Amount (£)</th>
                            </tr>
                            </thead>
                            <tbody>
                            {vcUserBreakdown.map((u, idx) => (
                                <tr key={idx}>
                                    <td>{u.user_name || "-"}</td>
                                    <td>
                                        {u.duration_minutes != null
                                            ? u.duration_minutes
                                            : "-"}
                                    </td>
                                    <td>
                                        {vcModalCentre
                                            ? formatMoney(
                                                u.standard_amount,
                                                vcModalCentre.cent_country
                                            )
                                            : u.standard_amount}
                                    </td>
                                    <td>
                                        {vcModalCentre
                                            ? formatMoney(
                                                u.additional_amount,
                                                vcModalCentre.cent_country
                                            )
                                            : u.additional_amount}
                                    </td>
                                    <td>
                                        {vcModalCentre
                                            ? formatMoney(
                                                u.total_amount,
                                                vcModalCentre.cent_country
                                            )
                                            : u.total_amount}
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </Table>
                    )}
                </Modal.Body>
            </Modal>
        </div>
    );
}
